import {
  formatAndCaptureAddress,
  getNextUrlDecision,
  getResponseMessageCode,
  getRouteTarget,
  getServiceabilityTrackingAttributes,
  getTypeaheadTrackingAttributes,
  type ServiceabilityRouteTarget,
} from "@/helpers/serviceability";
import { getDestinationTrackingAttributes } from "@/helpers/clickthroughDestinationTracking";
import { trackCustomEvent, trackErrorWithContext } from "@/lib/newrelic";
import fireFieldSelected, {
  FireFieldSelectedOptions,
} from "@/lib/tagular/fire-field-selected";
import { getAddress } from "@/services/api/serviceability/getAddress";
import { getServiceabilityV3 } from "@/services/api/serviceability/getServiceabilityV3";
import type { SessionStorageLocalizedAddress } from "@/types/common";
import type {
  ServiceabilityAddressSuggestion,
  ServiceabilitySearchStatus,
  ServiceabilitySubmitStatus,
  ServiceabilityV3Response,
} from "@/types/serviceability";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const RESPONSE_CODES = {
  // API Response Codes
  ERROR: "Error",
  BULK_MASTER_DWELL_TYPE: "BulkMasterDwellType",
  SUCCESS: "Success",
  UNSERVICEABLE: "OutOfFootPrint",
  // Custom Codes
  COULDNT_PARSE_CODE: "COULDNT_PARSE_CODE",
} as const;

interface UseServiceabilityState {
  inputValue: string;
  suggestions: ServiceabilityAddressSuggestion[];
  selectedAddress: ServiceabilityAddressSuggestion | null;
  searchStatus: ServiceabilitySearchStatus;
  submitStatus: ServiceabilitySubmitStatus;
  submitError: Error | null;
  nextUrl: string | null;
  responseCode: string | null;
  setInputValue: (value: string) => void;
  selectAddress: (
    address: ServiceabilityAddressSuggestion,
    eventDetails?: FireFieldSelectedOptions
  ) => void;
  submitSelection: () => Promise<void>;
  clearSelection: () => void;
  canSubmit: boolean;
}

export const isServiceableResponseCode = (responseCode: string | null) => {
  if (!responseCode) return false;

  return (
    responseCode === RESPONSE_CODES.SUCCESS ||
    responseCode === RESPONSE_CODES.BULK_MASTER_DWELL_TYPE
  );
};

const getNullableAddressValue = (
  serviceabilityValue: string | null | undefined,
  selectedValue: string
): string | null => serviceabilityValue ?? (selectedValue || null);

const getAddressValue = (
  serviceabilityValue: string | null | undefined,
  selectedValue: string
): string => serviceabilityValue ?? selectedValue;

const buildLocalizedAddress = (
  selectedAddress: ServiceabilityAddressSuggestion,
  serviceabilityAddress: ServiceabilityV3Response["address"]
): SessionStorageLocalizedAddress => ({
  address: {
    locationId: serviceabilityAddress?.locationId ?? selectedAddress.locationId,
    streetAddress1: getNullableAddressValue(
      serviceabilityAddress?.streetAddress1,
      selectedAddress.streetAddress1
    ),
    streetAddress2: getNullableAddressValue(
      serviceabilityAddress?.streetAddress2,
      selectedAddress.streetAddress2
    ),
    unitNumber: getAddressValue(
      serviceabilityAddress?.unitNumber,
      selectedAddress.unitNumber
    ),
    unitType: getAddressValue(
      serviceabilityAddress?.unitType,
      selectedAddress.unitType
    ),
    city: getAddressValue(serviceabilityAddress?.city, selectedAddress.city),
    state: getAddressValue(serviceabilityAddress?.state, selectedAddress.state),
    zipCode: getAddressValue(
      serviceabilityAddress?.zipCode,
      selectedAddress.zipCode
    ),
    searchReferenceId: serviceabilityAddress?.searchReferenceId ?? null,
    singleStreetAddress: serviceabilityAddress?.singleStreetAddress ?? null,
  },
});

const isOfferRoute = (
  routeTarget: ServiceabilityRouteTarget,
  nextUrl: string
): boolean => {
  if (routeTarget === "planBuilder") {
    return true;
  }

  try {
    return new URL(nextUrl).pathname.startsWith("/digital/offers");
  } catch {
    return nextUrl.startsWith("/digital/offers");
  }
};

const syncLocalizedAddress = (
  selectedAddress: ServiceabilityAddressSuggestion,
  serviceabilityAddress: ServiceabilityV3Response["address"],
  routeTarget: ServiceabilityRouteTarget,
  nextUrl: string
) => {
  try {
    if (isOfferRoute(routeTarget, nextUrl)) {
      sessionStorage.setItem(
        "localizedAddress",
        JSON.stringify(
          buildLocalizedAddress(selectedAddress, serviceabilityAddress)
        )
      );
    } else {
      sessionStorage.removeItem("localizedAddress");
    }
  } catch (error) {
    console.warn("[SERVICEABILITY] localizedAddress storage failed", error);
  }
};

export function useServiceability(
  destinationUrl?: string
): UseServiceabilityState {
  const [inputValue, setInputValueState] = useState("");
  const [suggestions, setSuggestions] = useState<
    ServiceabilityAddressSuggestion[]
  >([]);
  const [selectedAddress, setSelectedAddress] =
    useState<ServiceabilityAddressSuggestion | null>(null);
  const [searchStatus, setSearchStatus] =
    useState<ServiceabilitySearchStatus>("idle");
  const [submitStatus, setSubmitStatus] =
    useState<ServiceabilitySubmitStatus>("idle");
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [responseCode, setResponseCode] = useState<string | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const submitAbortControllerRef = useRef<AbortController | null>(null);

  const clearSelection = useCallback(() => {
    setSelectedAddress(null);
    setSubmitStatus("idle");
    setSubmitError(null);
    setNextUrl(null);
    setResponseCode(null);
  }, []);

  const setInputValue = useCallback(
    (value: string) => {
      searchAbortControllerRef.current?.abort();
      setInputValueState(value);

      const normalizedValue = value.trim();
      const hasSelectedAddress = selectedAddress !== null;
      const selectedFullAddress = selectedAddress?.fullAddress ?? "";

      if (normalizedValue.length < 3) {
        searchAbortControllerRef.current?.abort();
        setSuggestions([]);
        setSearchStatus("idle");
      }

      if (hasSelectedAddress && normalizedValue !== selectedFullAddress) {
        clearSelection();
      }
    },
    [clearSelection, selectedAddress]
  );

  const selectAddress = useCallback(
    (
      address: ServiceabilityAddressSuggestion,
      eventDetails?: FireFieldSelectedOptions
    ) => {
      setSelectedAddress(address);
      setInputValueState(address.fullAddress);
      setSuggestions([]);
      setSearchStatus("idle");
      setSubmitStatus("idle");
      setSubmitError(null);
      setNextUrl(null);
      setResponseCode(null);

      // track field selected
      if (eventDetails) {
        fireFieldSelected(eventDetails);
      }
    },
    []
  );

  useEffect(() => {
    const trimmedInput = inputValue.trim();
    const hasSelectedAddress =
      selectedAddress?.fullAddress.trim() === trimmedInput;

    if (trimmedInput.length < 3 || hasSelectedAddress) {
      searchAbortControllerRef.current?.abort();
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      searchAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      searchAbortControllerRef.current = abortController;
      setSearchStatus("searching");

      try {
        const nextSuggestions = await getAddress({
          search: trimmedInput,
          signal: abortController.signal,
        });

        setSuggestions(nextSuggestions);
        setSearchStatus(nextSuggestions.length > 0 ? "results" : "empty");
      } catch (error) {
        const nextError =
          error instanceof Error ? error : new Error("Unknown search error");

        if (nextError.name === "AbortError") {
          return;
        }

        setSuggestions([]);
        setSearchStatus("error");
        const trackingAttributes = {
          ...getTypeaheadTrackingAttributes(Boolean(selectedAddress)),
          errorName: nextError.name,
        };
        trackCustomEvent("serviceabilityTypeaheadFailed", trackingAttributes);
        trackErrorWithContext(nextError, trackingAttributes);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      searchAbortControllerRef.current?.abort();
    };
  }, [inputValue, selectedAddress]);

  useEffect(() => {
    return () => {
      searchAbortControllerRef.current?.abort();
      submitAbortControllerRef.current?.abort();
    };
  }, []);

  const submitSelection = useCallback(async () => {
    if (!selectedAddress) {
      const nextError = new Error("Selected address is required");
      setSubmitError(nextError);
      setSubmitStatus("error");
      setNextUrl(null);
      setResponseCode(null);
      return;
    }

    submitAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    submitAbortControllerRef.current = abortController;
    setSubmitStatus("submitting");
    setSubmitError(null);
    setNextUrl(null);
    setResponseCode(null);

    // Track address change
    formatAndCaptureAddress(selectedAddress);
    trackCustomEvent(
      "serviceabilityCheckStarted",
      getServiceabilityTrackingAttributes({ address: selectedAddress })
    );

    try {
      const nextServiceabilityResult = selectedAddress.elocId
        ? await getServiceabilityV3({
            elocId: selectedAddress.elocId,
            signal: abortController.signal,
          })
        : await getServiceabilityV3({
            serviceAddress: {
              zipCode: selectedAddress.zipCode,
              streetAddress1: selectedAddress.streetAddress1,
              unitNumber: selectedAddress.unitNumber,
            },
            signal: abortController.signal,
          });

      const routeTarget = getRouteTarget(nextServiceabilityResult);
      const destinationDecision = getNextUrlDecision(
        selectedAddress,
        nextServiceabilityResult,
        destinationUrl
      );
      const nextUrl = destinationDecision.url;
      const trackingAttributes = getServiceabilityTrackingAttributes({
        address: selectedAddress,
        response: nextServiceabilityResult,
        routeTarget,
      });

      if (!nextUrl) {
        const nextError = new Error(
          "Serviceability response did not map to a downstream route"
        );
        setSubmitError(nextError);
        setSubmitStatus("error");
        setNextUrl(null);
        setResponseCode(getResponseMessageCode(nextServiceabilityResult));
        trackCustomEvent(
          "serviceabilityUnexpectedResponse",
          trackingAttributes
        );
        trackErrorWithContext(nextError, trackingAttributes);
      } else {
        syncLocalizedAddress(
          selectedAddress,
          nextServiceabilityResult.address,
          routeTarget,
          nextUrl
        );
        setNextUrl(nextUrl);
        setSubmitStatus("success");
        setResponseCode(getResponseMessageCode(nextServiceabilityResult));
        trackCustomEvent("serviceabilityCheckSucceeded", trackingAttributes);
        trackCustomEvent("serviceabilityRouteDecided", {
          ...trackingAttributes,
          ...getDestinationTrackingAttributes(destinationDecision),
        });
      }
      console.log(
        "[SERVICEABILITY] serviceabilityV3",
        nextServiceabilityResult
      );
    } catch (error) {
      const nextError =
        error instanceof Error ? error : new Error("Unknown submit error");

      if (nextError.name === "AbortError") {
        return;
      }

      setSubmitError(nextError);
      setSubmitStatus("error");
      setNextUrl(null);
      const trackingAttributes = {
        ...getServiceabilityTrackingAttributes({ address: selectedAddress }),
        errorName: nextError.name,
        errorSource: "serviceabilityV3",
        serviceabilityError: "Failed to check address serviceability",
      };
      trackCustomEvent("serviceabilityCheckFailed", trackingAttributes);
      trackErrorWithContext(nextError, trackingAttributes);
      console.error("[SERVICEABILITY] submit error", nextError);
    }
  }, [selectedAddress, destinationUrl]);

  const canSubmit = useMemo(
    () =>
      Boolean(
        selectedAddress &&
        selectedAddress.streetAddress1 &&
        selectedAddress.zipCode
      ) && submitStatus !== "submitting",
    [selectedAddress, submitStatus]
  );

  return {
    inputValue,
    suggestions,
    selectedAddress,
    searchStatus,
    submitStatus,
    submitError,
    nextUrl,
    responseCode,
    setInputValue,
    selectAddress,
    submitSelection,
    clearSelection,
    canSubmit,
  };
}
